/**
 * useGalleryMarkup — drawings over a generator photo (gallery F-6; plan
 * docs/superpowers/plans/2026-09-28-gallery-f6-markup-dod.md §4.5).
 *
 * Reads are direct Firestore gets: firestore.rules lets every same-org employee (techs included)
 * read `generatorPhotoMarkup/{photoId}` and its `markupRevisions`, the same audience as the photo.
 * Writes go through the two P11 callables; nothing here writes Firestore directly.
 *
 * NEVER CUSTOMER-FACING. Drawings are for office and techs only (Nick: "no drawings should show on
 * customer photos"). This file is on the allow-list of scripts/guards/check-markup-isolation.mjs;
 * a customer page must not import it.
 *
 * - `useGalleryMarkup(photoId)` — the drawing, or `null` when the photo has none (revision 0).
 * - `useMarkupRevisions(photoId, currentRevision, enabled)` — the kept prior versions, newest first,
 *   read BY ID a page at a time and only while "Earlier versions" is open (a kept version can be
 *   ~0.5 MB; the editor never downloads all 20 just to show a list).
 * - `useSaveGalleryMarkup(photoId)` / `useClearGalleryMarkup(photoId)` — optimistic, rolled back on
 *   error, refetched on settle. A save names the revision it was drawn on; if someone else saved in
 *   between, the server refuses it; the editor then lays the user's changes over the newer drawing
 *   (markupEditorState rebase) and stays open.
 * - OFFLINE: drawings are NOT queued. A save while offline fails at once with a message that says
 *   so. (The app-wide offline message promises "your work is saved on this phone", which would be
 *   false here, so it is never used for markup.) Both writes carry NEEDS_SIGNAL_META, so the tech
 *   app's global offline backstop leaves them to their own message instead of toasting twice.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc, type Timestamp } from 'firebase/firestore';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import toast from 'react-hot-toast';
import { db, functions } from '../../config/firebase';
import { COLLECTIONS } from '../../config/collections';
import { useOrganization } from '../../contexts/OrganizationContext';
import { isDeviceOffline, isOfflineLikeFunctionsError, isServerUnavailableError } from '../../lib/offlineErrors';
import { NEEDS_SIGNAL_META } from '../../lib/needsSignalMutation';
import { galleryErrorMessage } from './useGeneratorGallery';
import {
  MARKUP_LIMITS,
  MARKUP_REVISIONS_SUBCOLLECTION,
  type MarkupShape,
} from '../../types/fieldService/generatorPhotoMarkupVocab';

/** generatorPhotoMarkup/{photoId} as the client reads it. */
export interface GalleryMarkup {
  photoId: string;
  organizationId: string;
  shapes: MarkupShape[];
  revision: number;
  restoredFrom: number | null;
  contentSha256: string | null;
  updatedBy: { uid: string; name: string; viaAgent: boolean } | null;
  updatedAt: Timestamp | null;
}

export type MarkupRevision = GalleryMarkup;

export interface MarkupWriteResult {
  revision: number;
  outcome: 'saved' | 'cleared' | 'already-applied' | 'no-op';
}

export const MARKUP_OFFLINE_MESSAGE = "You're offline, so the drawing wasn't saved. Try again when you have signal.";
export const MARKUP_SERVER_BUSY_MESSAGE = "We can't reach the server right now, so the drawing wasn't saved. Try again in a moment.";
export const MARKUP_CONFLICT_MESSAGE = 'Someone else just changed this drawing. Your changes were kept on top of theirs: check it, then save.';

/** Org is part of every key, so switching org never serves another org's drawing from cache. */
export const markupKeys = {
  all: ['generatorPhotoMarkup'] as const,
  detail: (organizationId: string, photoId: string) => [...markupKeys.all, organizationId, photoId] as const,
  revisions: (organizationId: string, photoId: string) =>
    [...markupKeys.all, organizationId, photoId, 'revisions'] as const,
};

const saveFn = httpsCallable<
  {
    organizationId: string;
    photoId: string;
    shapes: MarkupShape[];
    expectedRevision: number;
    mutationId: string;
    restoredFrom?: number | null;
  },
  MarkupWriteResult
>(functions, 'fieldService_galleryMarkupSave');

const clearFn = httpsCallable<
  { organizationId: string; photoId: string; expectedRevision: number; mutationId: string },
  MarkupWriteResult
>(functions, 'fieldService_galleryMarkupClear');

function toMarkup(photoId: string, data: Record<string, unknown>): GalleryMarkup {
  return {
    photoId,
    organizationId: String(data.organizationId ?? ''),
    shapes: Array.isArray(data.shapes) ? (data.shapes as MarkupShape[]) : [],
    revision: typeof data.revision === 'number' ? data.revision : 0,
    restoredFrom: typeof data.restoredFrom === 'number' ? data.restoredFrom : null,
    contentSha256: typeof data.contentSha256 === 'string' ? data.contentSha256 : null,
    updatedBy: (data.updatedBy as GalleryMarkup['updatedBy']) ?? null,
    updatedAt: (data.updatedAt as Timestamp | undefined) ?? null,
  };
}

/** A fresh id per save; the server accepts [A-Za-z0-9_-]{1,64}. */
export function newMutationId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * Plain-language failure for a markup write. Never the app-wide offline / server-busy texts: both
 * promise "your work is saved on this phone", which is false for a drawing.
 */
export function markupErrorMessage(err: unknown, fallback: string): string {
  if (isDeviceOffline() || isOfflineLikeFunctionsError(err)) return MARKUP_OFFLINE_MESSAGE;
  if (isServerUnavailableError(err)) return MARKUP_SERVER_BUSY_MESSAGE;
  if ((err as { code?: unknown } | null)?.code === 'functions/aborted') return MARKUP_CONFLICT_MESSAGE;
  return galleryErrorMessage(err, fallback);
}

/** The drawing over one photo, or null when it has none. `photoId` null disables the read. */
export function useGalleryMarkup(photoId: string | null) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  return useQuery({
    queryKey: markupKeys.detail(orgId, photoId ?? ''),
    enabled: Boolean(orgId && photoId),
    queryFn: async (): Promise<GalleryMarkup | null> => {
      const snap = await getDoc(doc(db, COLLECTIONS.GENERATOR_PHOTO_MARKUP, photoId as string));
      return snap.exists() ? toMarkup(snap.id, snap.data()) : null;
    },
  });
}

/** Kept prior versions shown per page of "Earlier versions". */
export const MARKUP_REVISION_PAGE = 5;

/** The kept revision ids, newest first, for a drawing now at `currentRevision` (the server keeps the last 20). */
export function keptRevisionIds(currentRevision: number): number[] {
  const floor = Math.max(1, currentRevision - MARKUP_LIMITS.revisionsKept);
  const ids: number[] = [];
  for (let r = currentRevision - 1; r >= floor; r -= 1) ids.push(r);
  return ids;
}

/**
 * Kept prior versions of a drawing, newest first, a page at a time. Each is a direct get by its
 * revision id (markupRevisions/{k}), so no query and no index; a version trimmed since is skipped.
 * Loaded only while `enabled` (the "Earlier versions" list is open).
 */
export function useMarkupRevisions(photoId: string | null, currentRevision: number, enabled: boolean) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const ids = keptRevisionIds(currentRevision);
  return useInfiniteQuery({
    queryKey: [...markupKeys.revisions(orgId, photoId ?? ''), currentRevision],
    enabled: Boolean(enabled && orgId && photoId && ids.length > 0),
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<MarkupRevision[]> => {
      const page = ids.slice(pageParam, pageParam + MARKUP_REVISION_PAGE);
      const snaps = await Promise.all(
        page.map((r) =>
          getDoc(doc(db, COLLECTIONS.GENERATOR_PHOTO_MARKUP, photoId as string, MARKUP_REVISIONS_SUBCOLLECTION, String(r)))
        )
      );
      return snaps
        .filter((snap) => snap.exists())
        .map((snap) => toMarkup(photoId as string, snap.data() as Record<string, unknown>))
        .filter((m) => m.organizationId === orgId);
    },
    getNextPageParam: (_last, _all, lastParam) =>
      lastParam + MARKUP_REVISION_PAGE < ids.length ? lastParam + MARKUP_REVISION_PAGE : undefined,
  });
}

interface SaveVars {
  shapes: MarkupShape[];
  expectedRevision: number;
  restoredFrom?: number | null;
}

function useMarkupCache(orgId: string, photoId: string) {
  const queryClient = useQueryClient();
  const key = markupKeys.detail(orgId, photoId);
  return {
    queryClient,
    key,
    async snapshot() {
      await queryClient.cancelQueries({ queryKey: key });
      return queryClient.getQueryData<GalleryMarkup | null>(key);
    },
    restore(previous: GalleryMarkup | null | undefined) {
      queryClient.setQueryData(key, previous);
    },
    invalidate() {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        queryClient.invalidateQueries({ queryKey: markupKeys.revisions(orgId, photoId) }),
      ]);
    },
  };
}

function optimistic(prev: GalleryMarkup | null | undefined, photoId: string, orgId: string, shapes: MarkupShape[], revision: number, restoredFrom: number | null): GalleryMarkup {
  return {
    photoId,
    organizationId: orgId,
    contentSha256: prev?.contentSha256 ?? null,
    updatedBy: prev?.updatedBy ?? null,
    updatedAt: prev?.updatedAt ?? null,
    shapes,
    revision,
    restoredFrom,
  };
}

export function useSaveGalleryMarkup(photoId: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const cache = useMarkupCache(orgId, photoId);

  return useMutation({
    meta: NEEDS_SIGNAL_META,
    mutationFn: async ({ shapes, expectedRevision, restoredFrom = null }: SaveVars) => {
      if (isDeviceOffline()) throw new Error(MARKUP_OFFLINE_MESSAGE);
      const payload = { organizationId: orgId, photoId, shapes, expectedRevision, mutationId: newMutationId() };
      return (await saveFn(restoredFrom ? { ...payload, restoredFrom } : payload)).data;
    },
    onMutate: async ({ shapes, expectedRevision, restoredFrom = null }) => {
      const previous = await cache.snapshot();
      cache.queryClient.setQueryData(cache.key, optimistic(previous, photoId, orgId, shapes, expectedRevision + 1, restoredFrom));
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      cache.restore(ctx?.previous);
      toast.error(markupErrorMessage(err, 'Could not save the drawing.'));
    },
    onSettled: () => cache.invalidate(),
  });
}

export function useClearGalleryMarkup(photoId: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';
  const cache = useMarkupCache(orgId, photoId);

  return useMutation({
    meta: NEEDS_SIGNAL_META,
    mutationFn: async ({ expectedRevision }: { expectedRevision: number }) => {
      if (isDeviceOffline()) throw new Error(MARKUP_OFFLINE_MESSAGE);
      return (await clearFn({ organizationId: orgId, photoId, expectedRevision, mutationId: newMutationId() })).data;
    },
    onMutate: async ({ expectedRevision }) => {
      const previous = await cache.snapshot();
      if (previous) cache.queryClient.setQueryData(cache.key, optimistic(previous, photoId, orgId, [], expectedRevision + 1, null));
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      cache.restore(ctx?.previous);
      toast.error(markupErrorMessage(err, 'Could not clear the drawing.'));
    },
    onSettled: () => cache.invalidate(),
  });
}
